import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from './useAuth';
import type { Campaign, Lead, Site, OutreachEmail, Commission, Payout } from '@shared/types';

type FirestoreData = Record<string, unknown>;

/**
 * Real-time collection hook using Firestore onSnapshot
 * Automatically updates when data changes in the database
 */
function useRealtimeCollection<T>(
  collectionName: string,
  constraints: QueryConstraint[] = [],
  enabled = true
) {
  const { user } = useAuth();
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || !user) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const q = query(
      collection(db, collectionName),
      where('userId', '==', user.uid),
      ...constraints
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })) as T[];
        setData(docs);
        setIsLoading(false);
      },
      (err) => {
        console.error(`Error in ${collectionName} subscription:`, err);
        setError(err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [collectionName, user?.uid, enabled, ...constraints.map(c => c.toString())]);

  // Provide a refetch function for compatibility
  const refetch = () => {
    // Real-time subscriptions auto-update, but we can trigger a re-render
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 100);
  };

  return { data, isLoading, error, refetch };
}

function useCollection<T>(
  collectionName: string,
  constraints: QueryConstraint[] = [],
  enabled = true
) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [collectionName, user?.uid, ...constraints.map(c => c.toString())],
    queryFn: async () => {
      if (!user) return [];

      const q = query(
        collection(db, collectionName),
        where('userId', '==', user.uid),
        ...constraints
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as T[];
    },
    enabled: enabled && !!user,
    retry: 1,
  });
}

function useDocument<T>(
  collectionName: string,
  documentId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: [collectionName, documentId],
    queryFn: async () => {
      if (!documentId) return null;

      const docRef = doc(db, collectionName, documentId);
      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) return null;

      return {
        id: snapshot.id,
        ...snapshot.data(),
      } as T;
    },
    enabled: enabled && !!documentId,
  });
}

export function useAddDocument(collectionName: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: FirestoreData) => {
      if (!user) throw new Error('Not authenticated');

      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        userId: user.uid,
        createdAt: serverTimestamp(),
      });

      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [collectionName] });
    },
  });
}

export function useUpdateDocument(collectionName: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<FirestoreData> }) => {
      const docRef = doc(db, collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [collectionName] });
      queryClient.invalidateQueries({ queryKey: [collectionName, id] });
    },
  });
}

export function useDeleteDocument(collectionName: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const docRef = doc(db, collectionName, id);
      await deleteDoc(docRef);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [collectionName] });
    },
  });
}

export function useCampaigns() {
  return useCollection<Campaign & { id: string }>('campaigns', [orderBy('createdAt', 'desc')]);
}

export function useCampaign(id: string | undefined) {
  return useDocument<Campaign & { id: string }>('campaigns', id);
}

export function useLeads(campaignId?: string, limitCount?: number) {
  const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];
  if (campaignId) {
    constraints.unshift(where('campaignId', '==', campaignId));
  }
  if (limitCount) {
    constraints.push(limit(limitCount));
  }
  // Use real-time subscription for leads to auto-update after scraping
  return useRealtimeCollection<Lead & { id: string }>('leads', constraints);
}

export function useLead(id: string | undefined) {
  return useDocument<Lead & { id: string }>('leads', id);
}

export function useSites() {
  // Use real-time subscription for sites to auto-update after generation
  return useRealtimeCollection<Site & { id: string }>('sites', [orderBy('createdAt', 'desc')]);
}

export function useSite(id: string | undefined) {
  return useDocument<Site & { id: string }>('sites', id);
}

export function useOutreachEmails() {
  return useCollection<OutreachEmail & { id: string }>('outreachEmails', [orderBy('createdAt', 'desc')]);
}

export function useCommissions() {
  // Use real-time subscription to auto-update when sites are sold
  return useRealtimeCollection<Commission & { id: string }>('commissions', [orderBy('createdAt', 'desc')]);
}

export function usePayouts() {
  // Use real-time subscription to auto-update when payouts are processed
  return useRealtimeCollection<Payout & { id: string }>('payouts', [orderBy('createdAt', 'desc')]);
}
